import { useState, useEffect } from "react";
import s from "../../../wizard.module.css";
import ModalAutorizado from "../../../Modals/ModalAuthorized";
import {
  Section,
  Field,
  TextField,
  SelectField,
  CedulaField,
  VehiculosField,
  PersonaCard,
  Pill,
  EmptyState,
} from "../../../FieldHelpers";
import { nombreDe, placasDe, inicialesDe } from "../../../persona";
import { useCedulaLookup } from "../../../../services/useCedulaLookup";
import { validateAutorizado } from "../../../../services/validationRules";
import { nuevaLlave } from "../../../../services/residentService";

const EMPTY = {
  id: null,
  cedula: "",
  nombre: "",
  apellidos: "",
  vehiculos: [],
  relacion: "Familiar",
};

export default function Autorizados({ setFormData, formData }) {
  const [autorizado, setAutorizado] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const { buscar, resultado, estado, reset } = useCedulaLookup();

  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Auto-rellenar nombre y apellidos con lo que devuelve el registro
  useEffect(() => {
    if (resultado) {
      setAutorizado((prev) => ({
        ...prev,
        nombre: resultado.nombre || prev.nombre,
        apellidos: resultado.apellidos || prev.apellidos,
      }));
    }
  }, [resultado]);

  function handleChange(e) {
    const { name, value } = e.target;
    setAutorizado((prev) => ({ ...prev, [name]: value }));
  }

  function agregar(e) {
    e.preventDefault();
    const errs = validateAutorizado(autorizado);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      autorizados: [...prev.autorizados, { ...autorizado, id: nuevaLlave() }],
    }));
    setAutorizado(EMPTY);
    setErrors({});
    reset();
  }

  function quitar(id) {
    setFormData((prev) => ({
      ...prev,
      autorizados: prev.autorizados.filter((a) => a.id !== id),
    }));
  }

  const total = formData.autorizados.length;

  return (
    <>
      <div className={s.columns}>
        {/* ── Formulario ── */}
        <div className={s.column}>
          <Section
            title="Datos del autorizado"
            subtitle="Ingrese la información del autorizado y su vehículo."
          >
            {Object.keys(errors).length > 0 && (
              <div className={s.alert} style={{ marginTop: 0 }}>
                <i className="fa-solid fa-triangle-exclamation" />
                Corregí los campos marcados antes de agregar el autorizado.
              </div>
            )}

            <div className={s.grid2}>
              <div className={s.colSpan2}>
                <CedulaField
                  value={autorizado.cedula}
                  onChange={handleChange}
                  onBlur={() => buscar(autorizado.cedula)}
                  onBuscar={() => buscar(autorizado.cedula)}
                  estado={estado}
                  error={errors.cedula}
                />
              </div>

              <TextField
                name="nombre"
                label="Nombre"
                required
                placeholder="Nombre"
                value={autorizado.nombre}
                onChange={handleChange}
                error={errors.nombre}
              />
              <TextField
                name="apellidos"
                label="Apellidos"
                required
                placeholder="Apellidos"
                value={autorizado.apellidos}
                onChange={handleChange}
                error={errors.apellidos}
              />

              <SelectField
                name="relacion"
                label="Relación"
                required
                value={autorizado.relacion}
                onChange={handleChange}
              >
                <option value="Familiar">Familiar</option>
                <option value="Inquilino">Inquilino</option>
                <option value="Empleado">Empleado</option>
              </SelectField>

              <Field
                label="Vehículo"
                hint={
                  autorizado.vehiculos.length ? (
                    <span className={s.labelHint}>{autorizado.vehiculos.length} asignada(s)</span>
                  ) : null
                }
              >
                <VehiculosField
                  vehiculos={autorizado.vehiculos}
                  errors={errors.vehiculos}
                  onChange={(vehiculos) => setAutorizado((prev) => ({ ...prev, vehiculos }))}
                />
              </Field>
            </div>

            <button type="button" className={`${s.btnPrimary} ${s.btnBlock}`} onClick={agregar}>
              <i className="fa-solid fa-plus" />
              Agregar Autorizado
            </button>
          </Section>
        </div>

        {/* ── Lista ── */}
        <div className={s.column}>
          <Section
            title={
              <>
                Autorizados agregados{" "}
                <span className={s.countBrand}>({total})</span>
              </>
            }
            action={
              total > 0 ? (
                <Pill tono="ok" redonda>
                  {total} registrado(s)
                </Pill>
              ) : null
            }
          >
            {total === 0 ? (
              <EmptyState>Todavía no hay autorizados. Los que agregues aparecen aquí.</EmptyState>
            ) : (
              <div className={s.personList}>
                {formData.autorizados.map((auth) => {
                  const placas = placasDe(auth);
                  return (
                    <PersonaCard
                      key={auth.id}
                      nombre={nombreDe(auth)}
                      avatar={inicialesDe(auth)}
                      pill={<Pill tono="brand">{auth.relacion}</Pill>}
                      meta={[auth.cedula, placas.join(", ")].filter(Boolean).join("  ·  ")}
                      onEdit={() => {
                        setSelectedIndex(auth.id);
                        setOpen(true);
                      }}
                      onDelete={() => quitar(auth.id)}
                    />
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      </div>

      <ModalAutorizado
        open={open}
        onClose={() => setOpen(false)}
        formData={formData}
        setFormData={setFormData}
        selectedIndex={selectedIndex}
      />
    </>
  );
}
